import PropTypes from 'prop-types'
import clsx from 'clsx'
import { connect } from 'react-redux'
import React, { useEffect } from 'react'
import ReactTooltip from 'react-tooltip'
import { ToastContainer } from 'react-toastify'
import { useHistory, useLocation } from 'react-router-dom'

import Navbar from './components/Navbar'
import Modals from './components/Modals'
import Routes from './components/Routers'

import http, { getAuthInfo } from './shared/http'
import { TApi, TRoutes } from './shared/const'

import 'normalize.css/normalize.css'
import './fonts/fonts.scss'
import './styles.scss'

import 'react-toastify/dist/ReactToastify.css'
import 'react-circular-progressbar/dist/styles.css'
import 'react-datepicker/dist/react-datepicker.css'

import style from './App.module.scss'
import { logIn } from './redux/ducks/user'
import { addOptions } from './redux/ducks/options'
import { loadPrivateOptions } from './shared/helpers'
import AlertTeacherMode from './components/AlertTeacherMode'
import PrivacyBanner from './components/PrivacyBanner'
import AppFooter, { shouldShowAppFooter } from './components/AppFooter'
import useCourseProgressImport from './shared/useCourseProgressImport'

import ConfirmDialog from './UI/ConfirmDialog'
import SupporterInvitationDialog from './UI/SupporterInvitationDialog'
import HelpButton from './UI/HelpButton'

/*
 * Adressen, die beim Laden stehen bleiben, statt aufs Dashboard zu springen.
 * Bewusst eine kurze, ausdrueckliche Liste: Jede Seite hier muss ohne
 * vorherige Navigation auskommen.
 */
const BLEIBT_STEHEN = [
	TRoutes.COMPANY_EMAIL_VERIFY,
	TRoutes.ANNUAL_REPORT,
	TRoutes.PUBLIC_ANNUAL_REPORT,
	TRoutes.PUBLIC_PLATFORM_REPORT,
	TRoutes.PUBLIC_DAVINCILAB,
	/*
	 * Die Unterstützer:innen-Registrierung öffnet sich aus dem Hinweis nach dem
	 * Herunterladen der Teilnahmeurkunde in einem neuen Tab — angemeldet als
	 * Schüler:in. Ohne diesen Eintrag landete der Tab auf dem Dashboard
	 * (Anforderung #212). Dasselbe gilt für den Link in der Urkunden-Mail.
	 */
	TRoutes.SUPPORTER_REGISTER
]

/*
 * Adressen mit einer Kennung darin lassen sich nicht in die Liste darüber
 * schreiben. Die Prüfseite einer Urkunde öffnet ein Betrieb über den QR-Code;
 * die Kursseiten öffnen Schüler:innen aus der Mail „Kurs abgeschlossen" und
 * nach dem Neuladen mitten in einem Video — beides soll dort bleiben. Für die
 * eingebetteten TalentsLounge-Kurse gilt dasselbe: ohne den Eintrag sprang
 * „Kurse" nach jedem Neuladen aufs Dashboard.
 * Eine Präsentation wird als Link weitergegeben und für das PDF direkt
 * aufgerufen (`?druck=1#3`) — auch sie bleibt stehen.
 */
const BLEIBT_STEHEN_PRAEFIX = ['/urkunde/', `${TRoutes.COURSES}/`, TRoutes.TL_COURSES, `${TRoutes.PRESENTATIONS}/`]

const bleibtStehen = pfad =>
	BLEIBT_STEHEN.includes(pfad) || pfad === TRoutes.COURSES || BLEIBT_STEHEN_PRAEFIX.some(praefix => pfad.startsWith(praefix))

function App(props) {
	const history = useHistory()
	const location = useLocation()

	/*
	 * Wer die Lernstrecke ohne Anmeldung begonnen hat, traegt den Stand im
	 * Browser mit sich. Sobald eine Anmeldung vorliegt, wandert er ins Konto.
	 */
	useCourseProgressImport()

	useEffect(() => {
		/*
		 * Das eigene Profil nur abrufen, wenn ueberhaupt eine Anmeldung
		 * vorliegt. Sonst lief der Aufruf auch auf der Anmeldeseite und
		 * antwortete dort mit 400 - sichtbar als roter Fehler in der Konsole,
		 * bevor sich jemand angemeldet hat.
		 */
		if (getAuthInfo().token) {
			http.get(TApi.COMMON_ME, {
				disableToast: true
			})
				.then(({ data }) => {
					props.logIn({
						role: data.role,
						profile: data
					})
					/*
					 * Nach dem Laden geht es auf das Dashboard — ausser die Seite
					 * wurde ueber einen Link geoeffnet und hat selbst etwas zu
					 * sagen. Die Bestaetigung der Firmen-E-Mail wurde hier bis
					 * 08.08.2026 sofort weggeschoben: Die Adresse war bestaetigt,
					 * gesehen hat das niemand. Die Junior-Bilanz steht in keinem
					 * Menue — wer ihren Verweis oeffnet, landete ohne diese
					 * Ausnahme auf dem Dashboard und kam gar nicht hin.
					 */
					if (!bleibtStehen(location.pathname)) history.push(TRoutes.DASHBOARD)
				})
				/*
				 * Vorher stand hier `.catch(console.error())` mit Klammern. Das
				 * ruft console.error sofort auf und uebergibt dessen Rueckgabe -
				 * undefined - als Handler. Der Fehler blieb damit unbehandelt und
				 * erschien als "Uncaught (in promise)".
				 */
				.catch(error => console.error('Profil konnte nicht geladen werden', error))
		}

		/*
		 * Öffentlich gibt es seit 14.10.2026 nur noch die Auswahllisten für
		 * Registrierung und Firmenpräsentation. Mit Anmeldung kommt der Rest
		 * dazu — nicht erst nach `/me`: Die Route braucht keine Rolle aus dem
		 * Store, nur die Anmeldung, und die steht schon im Kopfeintrag.
		 */
		http.get(TApi.COMMON_GET_OPTIONS).then(({ data }) => props.addOptions(data))
		if (getAuthInfo().token) loadPrivateOptions()

		/* eslint-disable-next-line */
	}, [])

	/*
	 * Die Rechts-Fusszeile liegt fixiert ueber dem Inhalt. Nur wo sie
	 * erscheint, wird unten auch Platz dafuer freigehalten - sonst bekaemen
	 * die oeffentlichen Seiten mit eigener (dunkler) Fusszeile einen weissen
	 * Streifen darunter.
	 */
	const withAppFooter = shouldShowAppFooter(props.isAuth, location.pathname)

	return (
		<div
			className={clsx(
				style.root,
				props.isTeacherStudentView && style.teacherMode,
				withAppFooter && style.withAppFooter
			)}
		>
			{props.isTeacherStudentView && <AlertTeacherMode />}
			<PrivacyBanner />
			<Navbar />
			<Routes />
			<AppFooter />
			{/*
			 * Der Hilfe-Knopf steht in jeder angemeldeten Ansicht der drei
			 * Endnutzer-Rollen. Er entscheidet selbst, ob er sich zeigt — hier
			 * haengt er nur im Baum.
			 */}
			<HelpButton />
			<Modals />
			<ConfirmDialog />
			<SupporterInvitationDialog />
			<ReactTooltip />
			<ToastContainer />
		</div>
	)
}

App.propTypes = {
	logIn: PropTypes.func.isRequired,
	addOptions: PropTypes.func.isRequired,
	isAuth: PropTypes.bool.isRequired,
	isTeacherStudentView: PropTypes.bool.isRequired
}

const mapStateToProps = state => ({
	isAuth: state.user.isAuth,
	isTeacherStudentView: state.user.isTeacherStudentView
})
const mapDispatchToProps = dispatch => ({
	logIn: data => dispatch(logIn(data)),
	addOptions: data => dispatch(addOptions(data))
})

export default connect(mapStateToProps, mapDispatchToProps)(App)
